'use client';

import { useEffect, useRef } from 'react';

import { SCRIPT_KEYS } from '@/shared/constants/scripts';
import { useScriptLoader } from '@/shared/contexts/ScriptLoaderContext';
import { useGtmUserInfo } from '@/shared/hooks/useGtmUserId';
import { onGentooFormSubmitted } from '@/shared/lib/gentoo';

import { LEAD_FORM_NAME } from '../constants/formName';
import { LEAD_TYPE } from '../constants/leadType';
import type { ProductType } from '../types/leadData';
import { pushLeadFormSubmit, type ItemCategory } from '../utils/leadFormGtm';

/** 젠투가 넘겨주는 사전 질문 답변 한 건. 키는 `p1`, `p2` … 로 순번이 붙는다. */
type GentooAnswer = { key?: string; answer?: string };

/**
 * 젠투 시나리오(`scenarioKey`) → 사업 종류(`item_category`) · 상품 종류(`item_category2`) 매핑.
 * 임대오피스·1인 오피스도 사업 라인은 공유오피스이고, 인테리어만 하이픈디자인으로 갈린다.
 */
const GENTOO_SCENARIO_ITEM = {
  office: { leadType: LEAD_TYPE.SHARED_OFFICE, productType: '공유오피스' },
  private: { leadType: LEAD_TYPE.SHARED_OFFICE, productType: '1인 오피스' },
  building: { leadType: LEAD_TYPE.POWERED_BY, productType: '임대오피스' },
  interior: {
    leadType: LEAD_TYPE.HYPHEN_DESIGN,
    productType: '사무실인테리어',
  },
} as const satisfies Record<
  string,
  { leadType: ItemCategory; productType: ProductType }
>;

/**
 * 젠투 상담 폼 제출 → 시트 row 38 `click_submit` 발사.
 *
 * SDK 콜백(`getFormSubmittedEvent`)은 사전 질문 답변과 시나리오 키를 넘겨준다. 실제 페이로드:
 * `{ scenarioKey: 'office', p1: { key: 'headcount', answer: '123' }, … }`
 * 순번 키는 질문 구성에 따라 달라지므로 `key` 로 찾는다.
 *
 * 지점 컨텍스트(`location_id` / `item_name`)는 콜백에 없어 판단이 불가능해 싣지 않는다.
 */
export function GentooFormSubmitTracker() {
  const { isLoaded } = useScriptLoader();
  const loaded = isLoaded(SCRIPT_KEYS.GENTOO);
  const { userId, email, phone } = useGtmUserInfo();

  // 콜백은 1회만 등록하므로 회원 정보는 ref 로 최신값을 읽는다 (등록 시점 값에 갇히지 않도록).
  const userInfoRef = useRef({ userId, email, phone });
  useEffect(() => {
    userInfoRef.current = { userId, email, phone };
  });

  const registeredRef = useRef(false);

  useEffect(() => {
    if (!loaded || registeredRef.current) return;
    registeredRef.current = true;

    onGentooFormSubmitted((params) => {
      const headcount = findAnswer(params, 'headcount');
      const area = findAnswer(params, 'area');
      const scenarioItem = findScenarioItem(params);
      const user = userInfoRef.current;

      pushLeadFormSubmit({
        btnName: '젠투',
        formName: LEAD_FORM_NAME.ZENTO,
        leadFormNum: 1,
        ...(scenarioItem && {
          leadType: [scenarioItem.leadType],
          productType: [scenarioItem.productType],
        }),
        // 시트 row 38 은 인원 수를 headcount/desk 두 키로 동일하게 요구한다.
        ...(headcount !== undefined && { headcount, desk: headcount }),
        ...(area !== undefined && { py: area }),
        // 비로그인이면 키를 빼지 않고 공란으로 보낸다 (시트 요구).
        email: user.email ?? '',
        phone: user.phone ?? '',
        ...(user.userId && { userId: user.userId }),
      });
    });
  }, [loaded]);

  return null;
}

function findAnswer(params: unknown, key: string): string | undefined {
  if (!params || typeof params !== 'object') return undefined;
  const answers = Object.values(
    params as Record<string, GentooAnswer | undefined>,
  );
  return answers.find((answer) => answer?.key === key)?.answer;
}

function findScenarioItem(params: unknown) {
  if (!params || typeof params !== 'object') return undefined;
  const key = (params as { scenarioKey?: unknown }).scenarioKey;
  if (typeof key !== 'string' || !(key in GENTOO_SCENARIO_ITEM)) {
    return undefined;
  }
  return GENTOO_SCENARIO_ITEM[key as keyof typeof GENTOO_SCENARIO_ITEM];
}
